import React, { useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  TablePagination
} from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroDataTable, WalteroButton } from '../waltero';

const ApiUsageDetailedLogs = ({ data, filters, onFiltersChange, onPageChange }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const columns = [
    {
      id: 'timestampRequestId',
      label: 'Timestamp',
      width: 180,
      render: (value) => {
        const timestamp = value?.split('#')[0];
        return timestamp ? new Date(timestamp).toLocaleString() : '-';
      }
    },
    {
      id: 'user_name',
      label: 'User',
      width: 200,
      accessor: (row) => row.user_name || row.userId,
      render: (value, row) => (
        <Box>
          <WalteroTypography variant="body2">{value || row.userId}</WalteroTypography>
          {row.user_email && (
            <WalteroTypography variant="caption" color="textSecondary">
              {row.user_email}
            </WalteroTypography>
          )}
        </Box>
      )
    },
    {
      id: 'method',
      label: 'Method',
      width: 80,
      render: (value) => (
        <Chip label={value} size="small" color="primary" />
      )
    },
    {
      id: 'endpoint',
      label: 'Endpoint',
      flex: 1
    },
    {
      id: 'status_code',
      label: 'Status',
      width: 100,
      type: 'number',
      render: (value) => (
        <Chip
          label={value}
          size="small"
          color={value < 400 ? 'success' : 'error'}
          variant={value < 400 ? 'outlined' : 'filled'}
        />
      )
    },
    {
      id: 'response_time_ms',
      label: 'Response Time',
      width: 120,
      align: 'right',
      type: 'number',
      render: (value) => `${value}ms`
    },
    {
      id: 'client_ip',
      label: 'Client IP',
      width: 130
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <WalteroCard>
        <Box sx={{ p: 2 }}>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="User ID"
                value={localFilters.userId || ''}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Endpoint"
                value={localFilters.endpoint || ''}
                onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Code</InputLabel>
                <Select
                  value={localFilters.statusCode || ''}
                  onChange={(e) => handleFilterChange('statusCode', e.target.value)}
                  label="Status Code"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="200">200 OK</MenuItem>
                  <MenuItem value="201">201 Created</MenuItem>
                  <MenuItem value="400">400 Bad Request</MenuItem>
                  <MenuItem value="401">401 Unauthorized</MenuItem>
                  <MenuItem value="403">403 Forbidden</MenuItem>
                  <MenuItem value="404">404 Not Found</MenuItem>
                  <MenuItem value="500">500 Server Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={localFilters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={localFilters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <WalteroButton variant="contained" onClick={handleApplyFilters}>
                Apply Filters
              </WalteroButton>
            </Grid>
          </Grid>

          {/* Data Table */}
          {data && data.data ? (
            <>
              <WalteroDataTable
                data={data.data || []}
                columns={columns}
                sortable={true}
                filterable={false}
                showControls={false}
              />
              {data.pagination && (
                <TablePagination
                  component="div"
                  count={-1}
                  page={data.pagination.page - 1}
                  onPageChange={(e, newPage) => onPageChange(newPage + 1)}
                  rowsPerPage={data.pagination.limit}
                  rowsPerPageOptions={[]}
                  labelDisplayedRows={() => `Page ${data.pagination.page}`}
                />
              )}
            </>
          ) : (
            <WalteroTypography variant="body1" color="textSecondary">
              No logs found. Try adjusting your filters.
            </WalteroTypography>
          )}
        </Box>
      </WalteroCard>
    </Box>
  );
};

export default ApiUsageDetailedLogs;